/* eslint-disable max-lines-per-function, max-lines, import/prefer-default-export */
import { track } from "jiffy-analytics";
import { isTouchDevice } from "common/utils/browser";
import { on as onEvent } from "common/utils/ui/event";
import SoftnessClearIcon from "assets/icons/softness-clear.svg?vanilla";
import {
  getSmartPlacement,
  adjustTooltipPosition,
  initStayOpenOnHover,
  initTooltipClose,
  stopBubbling,
  setupTouchTooltips
} from "components/compositions/shared-tooltip-utils";
import "./component.scss";

export { initStayOpenOnHover };

export const urlEnum = {
  print_compositions: "print_compositions"
};

const CLASS_NAMES = {
  JS_SCORE_TOOLTIP: ".js-softness-score-tooltip",
  JS_SCORE_NUMBER: ".js-softness-score-number",
  SOFTNESS_SCORE: ".softness-score",
  SOFTNESS_SCORE_MINI: ".softness-score-mini__number",
  LEARN_MORE: ".softness-score__learn-more"
};

const DPD_DYNAMIC_SOURCES = ["product_details_user_feedback", "product_color_picker"];

const softnessLevels = [
  { label: "Very soft", text: "Ultra-luxe softness" },
  { label: "Soft", text: "Smooth and comfy" },
  { label: "Everyday", text: "Basic comfort" },
  { label: "Rough", text: "Coarse feel" }
];

const tooltipContent = () => {
  const softnessItems = softnessLevels
    .map(
      ({ label, text }) => `
    <div class="flex items-center">
      <div class="w-20 h-6 bg-blue-700 rounded-2xl flex items-center justify-center mr-2">
        <span class="text-white text-sm font-semibold">${label}</span>
      </div>
      <span class="text-gray-600 text-sm">${text}</span>
    </div>
  `
    )
    .join("");

  return `
    <div
      class="w-[274px] bg-white border border-blue-700 rounded-lg shadow-lg"
      id="js-softness-score-tooltip-content"
    >
      <div class="bg-blue-700 rounded-t-lg px-[10.89px] py-[11px] flex items-center justify-between h-10">
        <div class="flex items-center">
          <div class="w-[16.33px] h-[18px] mr-[9px] js-icon-placeholder"></div>
          <h5 class="text-white text-sm font-bold leading-5">
            Softness Score
          </h5>
        </div>
        <a
          class="w-[16.33px] h-[18px] text-white js-softness-score-tooltip-close"
          role="button"
          tabindex="-1"
        ></a>
      </div>
      <div class="p-2">
        <div class="space-y-1">
          ${softnessItems}
        </div>
      </div>
    </div>
  `;
};
const tooltipTemplate = `
  <div class="tooltip softness-tooltip" role="tooltip">
    <div class="tooltip-inner p-0 bg-transparent border-0 rounded-lg max-w-none"></div>
  </div>`;

const $getTooltips = () => document.querySelectorAll(".js-softness-score-compact-tooltip");
const $$getTooltips = () => $(".js-softness-score-compact-tooltip");

const $getSoftnessTooltip = $element => {
  const $tooltip = $element
    .closest(CLASS_NAMES.JS_SCORE_TOOLTIP)
    ?.querySelector(CLASS_NAMES.JS_SCORE_NUMBER);

  if ($tooltip) {
    return $tooltip;
  }

  return (
    $element
      .closest(CLASS_NAMES.JS_SCORE_TOOLTIP)
      ?.querySelector(CLASS_NAMES.SOFTNESS_SCORE_MINI) || null
  );
};

const getPageType = () => {
  const currentUrl = window.location.href.replace(/\/+$/, ""); // Remove trailing slashes

  if (currentUrl.includes(".html")) {
    return "detail_page";
  }
  if (currentUrl === window.location.origin || currentUrl === `${window.location.origin}/`) {
    return "home_page";
  }
  return "listing_page";
};

let isLearnMoreOnEventAttached = false;
let activeSoftnessPayload = {};
let removeLearnMoreListener = null;
const initTrackLearnMoreClick = () => {
  if (isLearnMoreOnEventAttached) return;

  const callback = _ => {
    track("softness_score_learn_more_clicked", activeSoftnessPayload);
  };

  removeLearnMoreListener = onEvent("click", CLASS_NAMES.LEARN_MORE, callback, true);
  isLearnMoreOnEventAttached = true;
};

const trackSoftnessScoreBadgeOpened = $element => {
  const $softnessTooltip = $getSoftnessTooltip($element);
  const score = $softnessTooltip?.textContent.trim() || "";

  const dataset = $softnessTooltip?.dataset;
  const page = dataset?.source?.includes("page") ? dataset.source : getPageType();
  const source = dataset?.source || "left_nav";
  let compositionName = dataset?.compositionName || "";

  if (page === "detail_page" && DPD_DYNAMIC_SOURCES.includes(source)) {
    const urlParams = new URLSearchParams(window.location.search);
    compositionName = urlParams.get("ac") || compositionName;
  }

  const softnessPayload = {
    score,
    source,
    composition_name: compositionName,
    catalog_number: dataset?.catalogNumber || "",
    page
  };

  track("softness_score_badge_opened", softnessPayload);
  activeSoftnessPayload = softnessPayload;
};

const initTooltip = (elements = null) => {
  // If specific elements are provided, use them; otherwise get all tooltips
  const $$tooltips = elements ? $(elements) : $$getTooltips();

  // Filter out already initialized tooltips to prevent duplicates
  const $$uninitializedTooltips = $$tooltips.filter(function filterUninitialized() {
    return !$(this).data("bs.tooltip");
  });

  if ($$uninitializedTooltips.length === 0) return;

  // Unbind any existing event handlers to prevent duplicate bindings
  $$uninitializedTooltips.off("shown.bs.tooltip");

  $$uninitializedTooltips
    .tooltip({
      animation: false,
      container: "body",
      delay: {
        show: isTouchDevice() ? 0 : 500,
        hide: 100
      },
      html: true,
      sanitize: false, // Disable HTML sanitization to allow SVG elements
      title: tooltipContent(),
      template: tooltipTemplate,
      trigger: isTouchDevice() ? "manual" : "hover",
      placement(tooltip, element) {
        // Use smart placement that considers viewport boundaries
        return getSmartPlacement(tooltip, element);
      },
      boundary: "viewport", // Ensure tooltip stays within viewport
      fallbackPlacement: ["top", "bottom", "right", "left"] // Fallback order
    })
    .on("shown.bs.tooltip", function shownCallback() {
      $$tooltips.not(this).tooltip("hide");

      trackSoftnessScoreBadgeOpened(this);

      // Additional positioning adjustment after showing
      adjustTooltipPosition(this);

      // POST-INJECTION SVG ICON APPROACH
      // Why we need this workaround:
      // 1. Bootstrap tooltip sanitization: Even with sanitize:false, some versions of Bootstrap
      //    still filter out SVG elements or <use> tags for security reasons
      // 2. Template string timing: SVG sprites might not be fully loaded when the tooltip
      //    template is initially parsed
      // 3. Dynamic content limitations: Bootstrap's title/content options have limitations
      //    with dynamic SVG sprite references
      // 4. Browser security: Some browsers block SVG sprite references in dynamically
      //    inserted HTML for XSS protection
      //
      // This post-injection approach works because:
      // - The placeholder div passes all sanitization checks (it's just a div)
      // - SVG is injected after the tooltip is fully rendered and in the DOM
      // - Icon is rendered via vanilla SVG import (no sprite dependency)
      // - The innerHTML assignment happens on an already-trusted DOM element
      const iconPlaceholder = document.querySelector(".js-icon-placeholder");
      if (iconPlaceholder) {
        iconPlaceholder.innerHTML = SoftnessClearIcon({
          class: "w-[16.33px] h-[18px] text-white"
        });
        iconPlaceholder.classList.remove("js-icon-placeholder");
      }
    });

  // For touch devices, manually handle tooltip show/hide
  if (isTouchDevice()) {
    setupTouchTooltips($$uninitializedTooltips, $$getTooltips);
  }
};

// Handle window resize to reposition visible tooltips
let resizeTimeout;
const handleWindowResize = () => {
  clearTimeout(resizeTimeout);
  resizeTimeout = setTimeout(() => {
    // Find any visible tooltips and reposition them
    const visibleTooltips = document.querySelectorAll(".tooltip.show");
    visibleTooltips.forEach(tooltip => {
      const triggerId = tooltip.getAttribute("id");
      if (triggerId) {
        const triggerElement = document.querySelector(`[aria-describedby="${triggerId}"]`);
        if (triggerElement) {
          adjustTooltipPosition(triggerElement);
        }
      }
    });
  }, 150); // Debounce resize events
};

// MutationObserver to watch for dynamically added softness score elements
let observer = null;
const observerBubblingGuard = new WeakSet();

const initMutationObserver = () => {
  if (observer) return; // Already initialized

  observer = new MutationObserver(mutations => {
    const newTooltipElements = [];

    mutations.forEach(mutation => {
      // Check added nodes for tooltip elements
      mutation.addedNodes.forEach(node => {
        if (node.nodeType === 1) {
          // Element node
          // Check if the node itself is a tooltip element
          if (node.classList && node.classList.contains("js-softness-score-compact-tooltip")) {
            newTooltipElements.push(node);
          }
          // Check for tooltip elements within the added node
          const tooltips = node.querySelectorAll
            ? node.querySelectorAll(".js-softness-score-compact-tooltip")
            : [];
          tooltips.forEach(tooltip => newTooltipElements.push(tooltip));
        }
      });
    });

    // Initialize tooltips for newly added elements
    if (newTooltipElements.length > 0) {
      const currUrl = window.location.href;
      const isPrintCompositionsPage = currUrl.includes(urlEnum.print_compositions);

      if (!isPrintCompositionsPage) {
        initTooltip(newTooltipElements);
      }

      // Stop event bubbling for new elements (deduplicated via WeakSet)
      newTooltipElements.forEach($badge => {
        if (observerBubblingGuard.has($badge)) return;
        observerBubblingGuard.add($badge);

        $badge.addEventListener("click", e => {
          e.preventDefault();
          e.stopPropagation();
        });
      });
    }
  });

  // Start observing the body for changes
  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
};

let removeTooltipCloseListener = null;

const init = () => {
  const currUrl = window.location.href;

  const isPrintCompositionsPage = currUrl.includes(urlEnum.print_compositions);

  initStayOpenOnHover();

  if (!isPrintCompositionsPage) {
    initTooltip();
    initTrackLearnMoreClick();
  }

  removeTooltipCloseListener?.();
  removeTooltipCloseListener = initTooltipClose(
    ".js-softness-score-tooltip-close",
    $$getTooltips,
    ".js-softness-score-compact-tooltip"
  );
  stopBubbling($getTooltips);

  window.addEventListener("resize", handleWindowResize);

  initMutationObserver();
};

document.addEventListener("DOMContentLoaded", init, { once: true });

export const destroy = () => {
  if (observer) {
    observer.disconnect();
    observer = null;
  }

  window.removeEventListener("resize", handleWindowResize);
  clearTimeout(resizeTimeout);

  if (removeTooltipCloseListener) {
    removeTooltipCloseListener();
    removeTooltipCloseListener = null;
  }

  if (removeLearnMoreListener) {
    removeLearnMoreListener();
    removeLearnMoreListener = null;
    isLearnMoreOnEventAttached = false;
  }
};
